import { IS_CLIENT, __DEBUG__ } from '../config/constants';

const gtm = {
  // Track wether events are binded already or not.
  hasEventsAttached: false,

  // If GTM is not loaded we will push all events to this variable. Once GTM is
  // loaded we will concat this array with the GTM dataLayer variable.
  tempDataLayer: [],
};

// -----------------------------------------------------------------------------
//
// Define our internal helper functions.
//
// -----------------------------------------------------------------------------

/**
 * gtm.loaded
 *
 * @description
 *   Check wether google tag manager has been loaded.
 * @return {bool}
 *   A boolean wether the GTM is loaded or not.
 */
gtm.loaded = () => {
  return typeof window.google_tag_manager !== 'undefined';
};

/**
 * gtm.register
 *
 * @description
 *   Register a GTM helper function, used internally in the webapp.
 *
 * @param {string} fn
 *   The name of the function to register.
 * @param {function} callback
 *   The callback of the function being registered.
 * @return {void}
 */
gtm.register = (fn, callback) => {
  gtm[fn] = (...args) => {
    if (__DEBUG__) {
      console.info(`GTM Event: ${fn}() with args`, args);
    }

    callback(...args);
  };
};

/**
 * gtm.push
 *
 * @description
 *   A wrapper function to only pusb data into the dataLayer if GTM is loaded.
 * @param {object} data
 *   Event data to be sent to GTM.
 * @return {array|bool}
 *   An array containing the new dataLayer result or `false`.
 */
gtm.push = (data) => {
  if (gtm.loaded()) {
    return window.dataLayer.push(data);
  } else {
    gtm.tempDataLayer.push(data);
  }

  return false;
};

/**
 * gtm.attachEventListeners
 *
 * @description
 *   Attach event listeners related to GTM.
 * @return {void}
 */
gtm.attachEventListeners = () => {
  if (IS_CLIENT && !gtm.hasEventsAttached) {
    window.addEventListener('GTM-Loaded', () => {
      for (let i = 0, len = gtm.tempDataLayer.length; i < len; i++) {
        window.dataLayer.push(gtm.tempDataLayer[i]);
      }

      gtm.tempDataLayer = [];
    });

    gtm.hasEventsAttached = true;
  }
};

// -----------------------------------------------------------------------------
//
// Register our wrapper functions. We will use these throughout the app.
//
// -----------------------------------------------------------------------------
gtm.register('trackPage', (url) => {
  if (url) {
    gtm.push({
      event: 'Track page',
      location: url,
    });
  }
});

gtm.register('trackUserEvent', (category, action, label, value) => {
  gtm.push({
    event: 'Track user event',
    eventCategory: category,
    eventAction: action,
    eventLabel: label,
    eventValue: value,
  });
});

gtm.register('loadConsentRequiredTags', () => {
  gtm.push({
    event: 'Cookie consent: load tags on consent',
  });
});

// Initialize GTM.
gtm.attachEventListeners();

export default gtm;
